import {
  type ComposerDestination,
  type ComposerDestinationMode,
  type ComposerSpace,
  destinationKey,
} from '@shared/dream-components';

import type { CommunityPost, CommunitySpace } from '~/interfaces/community';

export const FEED_DESTINATION: ComposerDestination = { kind: 'feed' };

type SpaceSource = {
  id: string;
  slug: string;
  name: string;
  is_default?: boolean;
  gated?: boolean;
  read_only?: boolean;
};

type ResolveComposerDestinationArgs = {
  feedFirstEnabled: boolean;
  canCreatePost: boolean;
  canMovePost: boolean;
  spaces: CommunitySpace[];
  canPostIn: (space: CommunitySpace | null) => boolean;
  defaultSpaceSlug?: string;
  editPost?: CommunityPost | null;
  /**
   * A draft kept elsewhere, like a scheduled post, edited like a new post from its channel (null for the feed). A
   * channel that is gone is not offered, so it starts on the default.
   */
  initialDraft?: { space_slug: string | null } | null;
};

export type ResolvedComposerDestination = {
  destination: ComposerDestination;
  destinations: ComposerDestination[];
  mode: ComposerDestinationMode;
};

const toComposerSpace = (space: SpaceSource): ComposerSpace => ({
  id: space.id,
  slug: space.slug,
  name: space.name,
  is_default: space.is_default,
  gated: space.gated,
  read_only: space.read_only,
});

const channelDestination = (space: SpaceSource, disabled = false): ComposerDestination => ({
  kind: 'channel',
  space: { ...toComposerSpace(space), ...(disabled ? { disabled: true } : {}) },
});

export const resolveComposerDestination = ({
  feedFirstEnabled,
  canCreatePost,
  canMovePost,
  spaces,
  canPostIn,
  defaultSpaceSlug,
  editPost,
  initialDraft,
}: ResolveComposerDestinationArgs): ResolvedComposerDestination | null => {
  const postableSpaces = spaces.filter((space) => canPostIn(space));
  const channelOptions = postableSpaces.map((space) => channelDestination(space));

  if (editPost) {
    const editedSpace = editPost.space;
    const fromList = editedSpace ? spaces.find((space) => space.slug === editedSpace.slug) : undefined;
    const source = editedSpace ? (fromList ?? editedSpace) : undefined;
    const destination = source ? channelDestination(source) : FEED_DESTINATION;

    if (feedFirstEnabled && canMovePost) {
      const moveTargets = spaces.map((space) =>
        channelDestination(space, space.read_only === true && space.slug !== editedSpace?.slug),
      );
      const destinations = [FEED_DESTINATION, ...moveTargets];
      const currentKey = destinationKey(destination);
      const listed = destinations.some((option) => destinationKey(option) === currentKey);
      return {
        destination,
        destinations: listed ? destinations : [destination, ...destinations],
        mode: 'selectable',
      };
    }

    const mode: ComposerDestinationMode = !feedFirstEnabled && editedSpace ? 'hidden' : 'locked';
    return { destination, destinations: [destination], mode };
  }

  if (!canCreatePost) {
    return null;
  }

  const selectedSpaceSlug = initialDraft ? initialDraft.space_slug : defaultSpaceSlug;
  const defaultSpace = postableSpaces.find((space) => space.slug === selectedSpaceSlug);

  if (feedFirstEnabled) {
    return {
      destination: defaultSpace ? channelDestination(defaultSpace) : FEED_DESTINATION,
      destinations: [FEED_DESTINATION, ...channelOptions],
      mode: 'selectable',
    };
  }

  const fallbackSpace = defaultSpace ?? postableSpaces.find((space) => space.is_default) ?? postableSpaces[0];

  if (!fallbackSpace) {
    return null;
  }

  return {
    destination: channelDestination(fallbackSpace),
    destinations: channelOptions,
    mode: defaultSpaceSlug && !initialDraft ? 'hidden' : 'selectable',
  };
};
